import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Checkbox, Collapse, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { getCategoryColor, getCategoryLabel } from "../aiMappingColors";
import { sqftToSquareYards } from "../aiMappingDetections";
import { AiMappingSegmentationLike } from "../aiMappingTypes";
import { AiMappingController } from "../useAiMappingController";
import { CollapsibleSection, compactActionSx } from "./aiMappingResultsShared";

export interface SegmentationGroupSummary {
  color: string;
  categories: Set<string>;
  count: number;
  sqft: number;
  categoryLabel: string;
}

interface AiMappingSegmentationsSectionProps {
  controller: AiMappingController;
  areaId: string;
  activeSegmentations: AiMappingSegmentationLike[];
  segmentationGroups: SegmentationGroupSummary[];
  checkedSegmentations: AiMappingSegmentationLike[];
  sectionsOpen: boolean;
  onToggleSection: () => void;
  expandedSegGroups: Record<string, boolean>;
  onToggleSegGroup: (color: string) => void;
  onOpenBulkColor: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function AiMappingSegmentationsSection({
  controller,
  areaId,
  activeSegmentations,
  segmentationGroups,
  checkedSegmentations,
  sectionsOpen,
  onToggleSection,
  expandedSegGroups,
  onToggleSegGroup,
  onOpenBulkColor,
}: AiMappingSegmentationsSectionProps) {
  return (
    <CollapsibleSection
      title="Measured Areas"
      subtitle={`${activeSegmentations.length} measured areas`}
      open={sectionsOpen}
      onToggle={onToggleSection}
      actions={
        <IconButton
          size="small"
          title={controller.segmentationsVisible ? "Hide measured areas" : "Show measured areas"}
          onClick={() => controller.setSegmentationsVisible(!controller.segmentationsVisible)}
        >
          {controller.segmentationsVisible ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
      }
    >
      <MDBox display="flex" gap={0.75} mb={1} alignItems="center">
        <MDBox
          component="button"
          type="button"
          title={
            checkedSegmentations.length
              ? "Change color of selected areas"
              : "Change color for this Run Area"
          }
          disabled={controller.busy || activeSegmentations.length === 0}
          onClick={onOpenBulkColor}
          sx={{
            width: 30,
            height: 30,
            minWidth: 30,
            borderRadius: "999px",
            border: "1px solid #cbd5e1",
            background:
              "linear-gradient(135deg, #2563eb 0%, #22c55e 35%, #f59e0b 68%, #ef4444 100%)",
            opacity: activeSegmentations.length ? 1 : 0.55,
            cursor: activeSegmentations.length ? "pointer" : "not-allowed",
          }}
          data-testid="ai-mapping-recolor-segmentations"
        >
          <MDBox
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "999px",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.45)",
            }}
          />
        </MDBox>
      </MDBox>

      {checkedSegmentations.length > 0 && (
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          {checkedSegmentations.length} selected — Merge combines touching areas of the same
          category, Remove deletes them, Split divides a single area with a drawn line, and the
          color swatch recolors them.
        </MDTypography>
      )}

      {segmentationGroups.length === 0 ? (
        <MDTypography variant="body2" color="text" textAlign="center" mt={1}>
          No measured areas yet. Run segmentation to populate this section.
        </MDTypography>
      ) : (
        <MDBox display="flex" flexDirection="column" gap={0.75}>
          {segmentationGroups.map((group) => {
            const members = activeSegmentations.filter(
              (segmentation) =>
                getCategoryColor(segmentation.category, segmentation.color) === group.color
            );
            const expandedSegGroup = !!expandedSegGroups[group.color];
            return (
              <MDBox
                key={group.color}
                p={1}
                sx={{ border: "1px solid #e6eaef", borderRadius: "12px", bgcolor: "#fafbfc" }}
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ cursor: "pointer" }}
                  onClick={() => onToggleSegGroup(group.color)}
                >
                  <MDBox
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: group.color,
                      border: "1px solid rgba(0,0,0,0.08)",
                      flexShrink: 0,
                    }}
                  />
                  <MDBox sx={{ flex: 1, minWidth: 0 }}>
                    <MDTypography variant="button" fontWeight="medium">
                      {group.count} {group.count === 1 ? "area" : "areas"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ lineHeight: 1.15 }}
                    >
                      {group.categoryLabel}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text">
                    {Math.round(group.sqft).toLocaleString()} sqft ·{" "}
                    {Math.round(sqftToSquareYards(group.sqft)).toLocaleString()} sq yds
                  </MDTypography>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: expandedSegGroup ? "rotate(180deg)" : "none",
                      transition: "transform 150ms ease",
                    }}
                  />
                </MDBox>

                <Collapse in={expandedSegGroup}>
                  <MDBox pt={1} display="flex" flexDirection="column" gap={0.5}>
                    {members.map((segmentation) => (
                      <MDBox
                        key={segmentation.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        px={0.75}
                        py={0.5}
                        sx={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          bgcolor:
                            controller.selectedSegmentationId === segmentation.id
                              ? "rgba(25,118,210,0.08)"
                              : "#f8fafc",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                        }}
                        onClick={() =>
                          controller.selectSegmentation(
                            controller.selectedSegmentationId === segmentation.id
                              ? null
                              : segmentation.id
                          )
                        }
                        data-testid={`ai-mapping-segmentation-row-${segmentation.id}`}
                      >
                        <Checkbox
                          size="small"
                          sx={{ p: 0.25 }}
                          checked={controller.checkedSegmentationIds.includes(segmentation.id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => controller.toggleSegmentationChecked(segmentation.id)}
                          data-testid={`ai-mapping-select-segmentation-${segmentation.id}`}
                        />
                        <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                          {getCategoryLabel(segmentation.category)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {Math.round(segmentation.areaSqft ?? 0).toLocaleString()} sqft
                        </MDTypography>
                        <MDBox
                          component="label"
                          title="Change area color"
                          sx={{
                            width: 16,
                            height: 16,
                            minWidth: 16,
                            borderRadius: "999px",
                            border: "1px solid rgba(0,0,0,0.15)",
                            backgroundColor:
                              segmentation.color ?? getCategoryColor(segmentation.category),
                            cursor: controller.busy ? "not-allowed" : "pointer",
                            flexShrink: 0,
                            position: "relative",
                            overflow: "hidden",
                          }}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                          data-testid={`ai-mapping-recolor-segmentation-${segmentation.id}`}
                        >
                          <input
                            type="color"
                            value={segmentation.color ?? getCategoryColor(segmentation.category)}
                            disabled={controller.busy}
                            onChange={(event) =>
                              controller.updateSegmentation(areaId, segmentation.id, {
                                color: event.target.value,
                              })
                            }
                            style={{
                              position: "absolute",
                              inset: 0,
                              opacity: 0,
                              cursor: "pointer",
                              width: "100%",
                              height: "100%",
                              padding: 0,
                              border: "none",
                            }}
                          />
                        </MDBox>
                        <IconButton
                          size="small"
                          disabled={controller.busy}
                          title="Delete area"
                          sx={{ width: 28, height: 28 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            controller.deleteSegmentation(areaId, segmentation.id);
                          }}
                          data-testid={`ai-mapping-delete-segmentation-${segmentation.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </MDBox>
                    ))}
                  </MDBox>
                </Collapse>
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </CollapsibleSection>
  );
}
